import React, { useEffect, useMemo, useState } from "react";
import { Box, Grid, Typography } from "@mui/material";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import AppLoader from "../../components/appLoader";
import ConfirmationDialog from "../../components/confirmationDialog";
import CustomButton from "../../components/customButton";
import PaginatedTable from "../../components/dynamicTable";
import { openSnackAlert } from "../../helper";
import {
  addTaskSubTask,
  getTaskSubTasks,
  removeTaskSubTask,
} from "../../services/TaskManagement";
import AddSubtaskModal from "./addSubtaskModal";
import SubtaskImagesDialog from "./subtaskImagesDialog";
import SubtaskCommentsDialog from "./subtaskCommentsDialog";

const tableHeaders = [
  { title: "Location Name", value: "locationName", align: "center" },
  { title: "Location Address", value: "locationAddress", align: "center" },
  { title: "Location Number", value: "locationNumber", align: "center" },
  { title: "Instructions", value: "instructions", align: "center" },
  { title: "Time In", value: "timeIn", align: "center" },
  { title: "Time Out", value: "timeOut", align: "center" },
  { title: "Worker", value: "subtaskWorker", align: "center" },
  { title: "Worker Description", value: "workerDescription", align: "center" },
  { title: "Completed", value: "status", align: "center" },
  { title: "Images", value: "viewSubtaskImages", align: "center" },
  { title: "Comments", value: "viewSubtaskComments", align: "center" },
  // { title: "Status", value: "subtaskStatus", align: "center" },
  { title: "Actions", value: "actions", align: "center" },
];

const displayRows = tableHeaders.map((header) => header.value);

const SubtasksPage = () => {
  const navigate = useNavigate();
  const { jobId } = useParams();
  const location = useLocation();
  const [subtasks, setSubtasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [selectedSubtask, setSelectedSubtask] = useState(null);
  const [imagesDialogOpen, setImagesDialogOpen] = useState(false);
  const [commentsDialogOpen, setCommentsDialogOpen] = useState(false);
  const [viewSubtask, setViewSubtask] = useState(null);

  const jobName = location.state?.jobName || "";

  const fetchSubtasks = async () => {
    if (!jobId) return;
    try {
      setLoading(true);
      setError("");
      const response = await getTaskSubTasks(jobId);
      const apiResponse = response?.data;

      if (apiResponse?.status === "success") {
        setSubtasks(apiResponse?.data || []);
      } else {
        setSubtasks([]);
        const errorMessage = apiResponse?.message || "Failed to load subtasks";
        setError(errorMessage);
        openSnackAlert(errorMessage, "error");
      }
    } catch (err) {
      const errorMessage = err?.message || "Something went wrong while loading subtasks";
      setSubtasks([]);
      setError(errorMessage);
      openSnackAlert(errorMessage, "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubtasks();
  }, [jobId]);

  const tableRows = useMemo(
    () =>
      subtasks.map((subtask) => ({
        ...subtask,
        assignedWorker: subtask?.worker
          ? `${subtask.worker.firstName || ""} ${subtask.worker.lastName || ""}`.trim() || "N/A"
          : "N/A",
        subtaskStatus: subtask?.completed ? "Completed" : "Pending",
        timeIn: subtask?.timeIn || "-",
        timeOut: subtask?.timeOut || "-",
        status: subtask?.completed ? "completed" : "pending",
        workerDescription: subtask?.workerDescription?.trim() || "",
      })),
    [subtasks]
  );

  const handleViewSubtaskImages = (row) => {
    setViewSubtask(row);
    setImagesDialogOpen(true);
  };

  const handleViewSubtaskComments = (row) => {
    setViewSubtask(row);
    setCommentsDialogOpen(true);
  };

  const handleAddSubtask = async (payload) => {
    try {
      setLoading(true);
      const response = await addTaskSubTask(jobId, payload);
      const apiResponse = response?.data;

      if (apiResponse?.status === "success") {
        openSnackAlert(apiResponse?.message || "Subtask added successfully", "success");
        setAddModalOpen(false);
        await fetchSubtasks();
      } else {
        openSnackAlert(apiResponse?.message || "Failed to add subtask", "error");
      }
    } catch (err) {
      openSnackAlert(err?.message || "Something went wrong", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveSubtask = (row) => {
    setSelectedSubtask(row);
    setConfirmationOpen(true);
  };

  const handleConfirmRemoveSubtask = async () => {
    if (!selectedSubtask?._id) {
      setConfirmationOpen(false);
      return;
    }

    try {
      setLoading(true);
      const response = await removeTaskSubTask(jobId, selectedSubtask._id);
      const apiResponse = response?.data;
      if (apiResponse?.status === "success") {
        openSnackAlert(apiResponse?.message || "Subtask removed successfully", "success");
        setConfirmationOpen(false);
        setSelectedSubtask(null);
        await fetchSubtasks();
      } else {
        openSnackAlert(apiResponse?.message || "Failed to remove subtask", "error");
      }
    } catch (err) {
      openSnackAlert(err?.message || "Something went wrong", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box bgcolor="primary.bgLight" padding="20px" borderRadius="22px">
      <AppLoader isVisible={loading} />
      <Grid container spacing={2} mb={2}>
        <Grid item xs={12} md={8}>
          <Typography variant="h4">Subtask Management</Typography>
          <Typography variant="description">
            Job: {jobName || jobId}
          </Typography>
        </Grid>
        <Grid item xs={12} md={4}>
          <Box display="flex" gap={1} justifyContent={{ xs: "flex-start", md: "flex-end" }}>
            <CustomButton
              btnLabel="Back"
              handlePressBtn={() => navigate("/task-management")}
              btnBgColor="#fff"
              btnTextColor="#225B27"
              borderColor="#225B27"
              isBorder="1px solid #225B27"
            />
            <CustomButton
              btnLabel="Add Subtask"
              handlePressBtn={() => setAddModalOpen(true)}
              btnBgColor="#225B27"
              btnTextColor="#fff"
            />
          </Box>
        </Grid>
      </Grid>

      {error ? (
        <Box
          bgcolor="#fff"
          borderRadius="18px"
          border="1px solid #f5c2c7"
          px={2}
          py={3}
        >
          <Typography color="error.main" fontSize={14}>
            {error}
          </Typography>
        </Box>
      ) : (
        <PaginatedTable
          tableHeader={tableHeaders}
          tableData={tableRows}
          displayRows={displayRows}
          showPagination={false}
          onDelete={handleRemoveSubtask}
          handleViewSubtaskImages={handleViewSubtaskImages}
          handleViewSubtaskComments={handleViewSubtaskComments}
        />
      )}

      <AddSubtaskModal
        open={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        onSubmit={handleAddSubtask}
        loading={loading}
      />

      <ConfirmationDialog
        open={confirmationOpen}
        onClose={() => {
          setConfirmationOpen(false);
          setSelectedSubtask(null);
        }}
        onConfirm={handleConfirmRemoveSubtask}
      />

      <SubtaskImagesDialog
        open={imagesDialogOpen}
        onClose={() => {
          setImagesDialogOpen(false);
          setViewSubtask(null);
        }}
        subtask={viewSubtask}
      />

      <SubtaskCommentsDialog
        open={commentsDialogOpen}
        onClose={() => {
          setCommentsDialogOpen(false);
          setViewSubtask(null);
        }}
        subtask={viewSubtask}
      />
    </Box>
  );
};

export default SubtasksPage;
